import { Settings, Layers, Users, ListChecks, Sliders, Shield, GitBranch, Trash2, BarChart3, Bell, KeyRound, Building2 } from "lucide-react";
import { Link } from "wouter";
import { PageHeading, Section, Callout, RoleBadge, pathFor, Diagram, MockTabs } from "../shared";

export default function AdminTabsSection() {
  return (
    <div data-testid="guide-admin-tabs">
      <PageHeading
        icon={Settings}
        title="Admin Tabs — Overview"
        description="The Admin page is the control room for the system. It has lots of tabs grouped into three families: everyday operations, the deeper governance rules, and a couple of system housekeeping tools."
      />

      <Callout variant="info" title="In plain words">
        Think of the Admin page as a filing cabinet with three drawers. The top drawer (<span className="text-white">Operations</span>) is for day-to-day jobs like adding people and reading performance numbers. The middle drawer (<span className="text-white">Governance</span>) is where senior admins set the rules of how work flows. The bottom drawer (<span className="text-white">System</span>) holds the reminder schedule and the recycle bin. You only see the tabs your role is allowed to use — anything you can't access is simply hidden.
      </Callout>

      <Section title="When do I use this?">
        <p className="text-sm text-white/65 leading-relaxed">
          Come here when you need to manage people, change how the system behaves, check performance, or recover something that was deleted. Use the cards below to jump to a full plain-English guide for each tab.
        </p>
      </Section>

      <Section title="The tabs along the top of the Admin page">
        <Diagram
          frameLabel="Admin"
          legend={[
            <><span className="text-white">Operations</span> tabs — KPI Dashboard, Org Chart, User Accounts, Departments. Open to General Admin and above.</>,
            <><span className="text-white">Governance</span> tabs — Versions, Lanes, Actors, Reference Lists and the permission rules. Super Admin only.</>,
            <><span className="text-white">System</span> tabs — Reminder Schedule and Deleted Items. IT Admin and above.</>,
          ]}
        >
          <MockTabs
            tabs={[
              { label: "KPI Dashboard", active: true, marker: 1 },
              { label: "Org Chart" },
              { label: "User Accounts" },
              { label: "Versions", marker: 2 },
              { label: "Lanes" },
              { label: "Reminder Schedule", marker: 3 },
              { label: "Deleted Items" },
            ]}
          />
        </Diagram>
      </Section>

      <Section title="Operations">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <TabCard slug="admin/kpi" icon={BarChart3} color="text-cyan-300" name="KPI Dashboard" role="admin">
            Per-person performance numbers — how many items each person handled, average time to close, and any breaches. (Excel exports of action items live on the Register page itself, not here.)
          </TabCard>
          <TabCard slug="admin/team-members" icon={Users} color="text-blue-300" name="Team Members" role="admin">
            The list of people who can be given work, plus the chain-of-command that draws the org chart. A team member is just a name — not a login.
          </TabCard>
          <TabCard slug="admin/user-accounts" icon={Users} color="text-blue-300" name="User Accounts" role="admin">
            The actual logins. Create users, give them a role, reset passwords, and link them to a team-member name.
          </TabCard>
          <TabCard slug="admin/departments" icon={Building2} color="text-blue-300" name="Departments & Sections" role="admin">
            A simple two-level org tree. Captured now for future inter-departmental tracking; not yet wired into the Register form or KPIs.
          </TabCard>
        </div>
      </Section>

      <Section title="Governance configuration">
        <div className="flex items-center gap-2 mb-3">
          <RoleBadge role="super_admin" />
          <span className="text-xs text-white/40">All edits happen on a draft version. Activate it to switch it on.</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <TabCard slug="admin/versions" icon={GitBranch} color="text-purple-300" name="Versions" role="super_admin">
            Make a draft copy of the current rules, edit it safely, then turn it on. Once switched on, a version is locked as read-only.
          </TabCard>
          <TabCard slug="admin/lanes" icon={Layers} color="text-purple-300" name="Authority Lanes" role="super_admin">
            The routing categories for work — name, key, who reviews, who signs off.
          </TabCard>
          <TabCard slug="admin/actors" icon={Users} color="text-purple-300" name="Authority Actors" role="super_admin">
            The people or bodies who review and sign off inside a lane.
          </TabCard>
          <TabCard slug="admin/reference-lists" icon={ListChecks} color="text-purple-300" name="Reference Lists" role="super_admin">
            The dropdown choices used across the app — priorities, origin types, document types, meeting types.
          </TabCard>
          <TabCard slug="admin/workflow-rules" icon={Sliders} color="text-purple-300" name="Workflow Rules" role="super_admin">
            Per-lane rules — which moves are allowed and when a sign-off is required.
          </TabCard>
          <TabCard slug="admin/field-permissions" icon={Sliders} color="text-purple-300" name="Field Permissions" role="super_admin">
            Who can edit which fields, in which role, during which stage of the work.
          </TabCard>
          <TabCard slug="admin/transition-permissions" icon={Shield} color="text-purple-300" name="Transition Permissions" role="super_admin">
            Who is allowed to move items into a particular status. An empty list means it's open to all admins.
          </TabCard>
          <TabCard slug="admin/roles-permissions" icon={KeyRound} color="text-purple-300" name="Roles & Permissions" role="super_admin">
            One card per page with a single access-level picker. Build custom roles and see an audit log of every change.
          </TabCard>
        </div>
      </Section>

      <Section title="System">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <TabCard slug="admin/reminder-schedule" icon={Bell} color="text-amber-300" name="Reminder Schedule" role="it_admin">
            When reminder emails go out before and after a due date, who receives them, and a log of every reminder sent.
          </TabCard>
          <TabCard slug="admin/deleted-items" icon={Trash2} color="text-amber-300" name="Deleted Items" role="it_admin">
            The recycle bin for deleted action items, with a Restore button. Nothing is ever truly erased from the screen.
          </TabCard>
        </div>
        <Callout variant="info">
          Email setup, branding, backups, agent API keys, Live Refresh, the System Diagnostics Log and the IT Alert Dispatcher are <em>not</em> here — they live on the separate <Link href={pathFor("settings-tabs")} className="text-cyan-300 hover:underline">Settings page</Link>.
        </Callout>
      </Section>
    </div>
  );
}

function TabCard({ slug, icon: Icon, color, name, role, children }: { slug: string; icon: any; color: string; name: string; role: string; children: React.ReactNode }) {
  return (
    <div className="p-3 rounded-md bg-white/[0.02] border border-white/5" data-testid={`admin-tab-${name.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon className={`w-4 h-4 ${color}`} />
        <p className="text-sm text-white font-medium">{name}</p>
        <RoleBadge role={role} />
      </div>
      <p className="text-xs text-white/55 leading-relaxed mb-2">{children}</p>
      <Link href={pathFor(slug)} className="text-xs text-cyan-300 hover:underline" data-testid={`link-readmore-${slug.replace(/\//g, "-")}`}>Read more →</Link>
    </div>
  );
}
